<script lang="ts">
	import { ListMenuCardV2 } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { createEventDispatcher } from 'svelte';

	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { camera_previews } from 'src/stores/features/cameras/previews';
	import VideoContainer from '../CameraPlayer/VideoContainer.svelte';
	import WebRtcPlayer from '../CameraPlayer/WebRTCPlayer.svelte';
	import CameraTileWidgetCard from './CameraTileWidgetCard.svelte';
	import { getDeviceLastSeenData } from 'src/helpers/device_state_view_helpers';
	import { computeCameraState, computeStreamState } from '../helpers/camera-state-helpers';
	import { getCameraComponentKey } from '../helpers/camera-helpers';
	import type { CameraStreamer } from '../types/camera-status';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { requestVideoFullscreen } from '../helpers/camera-helpers';
	import { buildCloudMediaUrl } from 'src/services/cam-cloud/recordings';
	import type { Device } from 'src/types/Device';

	export let camera: Device;
	export let editMode: boolean = false;
	export let mediaFit: 'cover' | 'contain' = 'cover';
	/** Dashboard embed: minimal chrome, ellipsis opens device drawer. */
	export let embedMode = false;
	/** Media only — no tile overlay (parent card supplies status chrome). */
	export let backgroundOnly = false;
	export let hideHeaderActions = false;
	export let hideBottomActions = false;
	/** Dashboard widgets: fill container height instead of fixed 16:9 only. */
	export let fillContainer = false;
	/** Semi-transparent status header (motion, battery, live, etc.). */
	export let showDashboardHeader = false;
	export let headerCompact = false;
	export let denseOverlay = false;
	export let useCloudConnectionIcon = false;
	export let showNameOnHover = false;

	const dispatch = createEventDispatcher();

	// Desktop/mobile detection for drag behavior
	let windowInnerWidth: number;
	$: isDesktop = windowInnerWidth > SystemScreenBreakpoints.TABLET_MAX;

	// Desktop: entire tile is draggable in edit mode
	function handleDesktopDragStart(e: MouseEvent | TouchEvent) {
		if (!editMode || !isDesktop) return;
		dispatch('handle-drag-start', e);
	}

	function handleDesktopDragEnd(e: MouseEvent | TouchEvent) {
		if (!editMode || !isDesktop) return;
		dispatch('handle-drag-end', e);
	}

	// Derived state (use real_id — status/settings are keyed like other dashboard cards)
	$: deviceId = real_id(camera.id);
	$: statusStore = getDeviceStatus(deviceId);
	$: settingsStore = getDeviceSettings(deviceId);
	$: status = $statusStore;
	$: settings = $settingsStore;
	$: cameraKey = getCameraComponentKey(camera);
	$: isOnline = status?.online ?? false;
	$: isPrivacyMode = settings?.[cameraKey]?.privacy ?? false;
	$: streamer = (status?.[cameraKey] as { streamer?: CameraStreamer } | undefined)?.streamer;
	$: lastSeenData = getDeviceLastSeenData(camera, status, settings);

	// Build authenticated preview URL
	let previewImage = '';
	$: {
		const baseUrl = $camera_previews?.[deviceId]?.medium;
		if (baseUrl) {
			buildCloudMediaUrl(baseUrl).then((url) => (previewImage = url));
		} else {
			previewImage = '';
		}
	}

	// Loaded preview image URL for edit mode
	$: previewImageUrl = previewImage ? load_image_from(previewImage) : '';

	const defaultPlaceholderSrc = load_image_from('images/device_images/logo_white_S.png');

	// Tile sizing based on measured tile width (bind:clientWidth).
	// Accounts for sidebars, padding, and gaps automatically.
	type TileSize = 's' | 'm' | 'l';
	type AvatarSize = 's' | 'm' | 'm_plus' | 'l' | 'xl' | 'xxl';
	let tileWidth = 0;
	function getTileConfig(width: number): { size: TileSize; avatarSize: AvatarSize; compact: boolean } {
		if (width >= 500) return { size: 'l', avatarSize: 'xl', compact: false };
		if (width >= 300) return { size: 'm', avatarSize: 'l', compact: true };
		return { size: 's', avatarSize: 'm', compact: true };
	}
	$: tileConfig = getTileConfig(tileWidth);
	$: tileSize = tileConfig.size;
	$: avatarSize = tileConfig.avatarSize;
	$: compact = tileConfig.compact;

	// Format last seen time for display
	$: lastSeenTime = lastSeenData?.lastSeenUpdate
		? capFirstChar(
				$format('text_last_active', {
					default: `Last active: ${lastSeenData.time}.`,
					values: { time: lastSeenData.time },
				}),
			)
		: '';

	function handleOpenDevice() {
		setSidePanelState({ type: 'device', id: camera.id.toString(), open: true });
		dispatch('openDevice');
	}

	// WebRTCPlayer streaming state (bound)
	let streamLoading = true;
	let streamError = { heading: '', message: '' };
	let webrtcPlayer: WebRtcPlayer;

	// Blocked states (prevent WebRTCPlayer from mounting): privacy / offline /
	// streamer not running. WebRTC stream state only applies when not blocked.
	$: cameraState = computeCameraState(isPrivacyMode, isOnline, streamer);
	$: streamState = computeStreamState(streamLoading, streamError);
	$: videoState = cameraState ?? streamState;

	// Stream is actually playable (no camera-level block)
	// TODO: Optimization required
	$: isStreaming = !cameraState;

	$: tilePreviewImage = previewImage
		? load_image_from(previewImage)
		: showDashboardHeader && cameraState?.type === 'offline'
			? defaultPlaceholderSrc
			: '';

	// Fullscreen: query video element and request fullscreen
	let tileElement: HTMLDivElement;

	function handleFullscreen() {
		const videoElement = tileElement?.querySelector('video') as HTMLVideoElement | null;
		requestVideoFullscreen(videoElement);
	}
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if editMode}
	<!-- Edit mode: uses ListMenuCardV2 for drag-and-drop -->
	<div
		class="camera-tile-root"
		bind:clientWidth={tileWidth}
		role="presentation"
		on:mousedown={handleDesktopDragStart}
		on:mouseup={handleDesktopDragEnd}
		on:touchend={handleDesktopDragEnd}
	>
		<ListMenuCardV2
			class="camera-tile-edit-card {!isOnline && previewImageUrl
				? 'is-offline'
				: ''} {!previewImageUrl ? 'no-image' : ''}"
			title={camera.name}
			image={previewImageUrl || defaultPlaceholderSrc}
			editMode={true}
			type="extended"
			a11yProps={{
				dndMobileHandleAriaLabel: $format('aria_drag_camera', { default: 'Drag to reorder' }),
			}}
			on:handle-drag-start
			on:handle-drag-end
		/>
	</div>
{:else}
	<!-- Normal mode: delegate to widget for presentation -->
	<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
	<div class="camera-tile-root" bind:this={tileElement} bind:clientWidth={tileWidth}>
		<CameraTileWidgetCard
			name={camera.name}
			isOnline={isStreaming}
			size={tileSize}
			embedMode={embedMode || backgroundOnly}
			{fillContainer}
			hideHeaderActions
			hideBottomActions={backgroundOnly}
			hideOverlay={backgroundOnly}
			showFullscreen={!backgroundOnly && !editMode}
			{showDashboardHeader}
			headerDevice={backgroundOnly ? undefined : camera}
			headerStatus={status}
			headerSettings={settings}
			showLiveBadge={showDashboardHeader && isStreaming && !useCloudConnectionIcon}
			{headerCompact}
			{denseOverlay}
			useCloudConnectionIcon={useCloudConnectionIcon || denseOverlay}
			{showNameOnHover}
			on:openDevice={handleOpenDevice}
			on:fullscreen={handleFullscreen}
		>
			<svelte:fragment slot="media">
				<VideoContainer
					state={videoState}
					previewImage={tilePreviewImage}
					previewImageFit={mediaFit}
					lastSeenTime={backgroundOnly ? '' : lastSeenTime}
					{avatarSize}
					isZoomable={!embedMode && !backgroundOnly}
					hasZoomCtrlShortcut={!embedMode && !backgroundOnly}
					compact={compact || backgroundOnly}
					minimalBlockedState={backgroundOnly}
				>
					{#if isStreaming}
						<WebRtcPlayer
							bind:this={webrtcPlayer}
							deviceId={camera.id}
							showControls={false}
							objectFit={mediaFit}
							bind:isLoading={streamLoading}
							bind:errorMessage={streamError}
						/>
					{/if}
				</VideoContainer>
			</svelte:fragment>
		</CameraTileWidgetCard>
	</div>
{/if}

<style>
	/* Edit mode: ListMenuCardV2 overrides */
	:global(.camera-tile-edit-card.list-menu-card-v2) {
		min-height: unset !important;
		aspect-ratio: 16 / 9;
		width: 100%;
		height: 100%;
		flex-shrink: 1;
		border-radius: var(--radius-extra-big);
		overflow: hidden;
	}

	:global(.camera-tile-edit-card.is-offline) {
		filter: grayscale(100%);
	}

	:global(.camera-tile-edit-card.no-image) {
		background-color: var(--color-2);
	}

	.camera-tile-root {
		width: 100%;
		height: 100%;
		min-width: 0;
		min-height: 0;
	}
</style>
